import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import type { SortField } from '../../types/sort';
import { BaseEntity } from './base.entity';
import type { ListSavedSearchRoleEntity } from './list-saved-search-role.entity';
import { UserEntity } from './user.entity';

export type ListSavedSearchVisibility = 'private' | 'role' | 'organization';

/**
 * FilterRule shape stored on saved searches. Mirrors the FilterRule interface
 * from @supply-mover-2-0/core but typed loosely here (Record<string, unknown>)
 * to avoid an entity → core dependency. The list-config and saved-search
 * services cast to/from the concrete FilterRule type at the boundaries.
 */
export type SavedSearchFilterRule = Record<string, unknown>;

@Index('idx_list_saved_search_list_key', ['listKey'])
@Index('idx_list_saved_search_manager', ['fkManagerUserId'])
@Index('idx_list_saved_search_visibility', ['visibility'])
@Index('idx_list_saved_search_active', ['isActive'])
@Index('idx_list_saved_search_created', ['createdDate'])
@Entity('list_saved_search')
export class ListSavedSearchEntity extends BaseEntity {
	@Column('varchar', { name: '_name', length: 255 })
	public name!: string;

	@Column('text', { name: '_description', nullable: true })
	public description!: string | null;

	@Column('varchar', { name: '_list_key', length: 100 })
	public listKey!: string;

	@Column('varchar', { name: '_base_list_id', length: 100, nullable: true })
	public baseListId!: string | null;

	@Column('json', { name: '_filter_state' })
	public filterState!: Record<string, unknown>;

	@Column('varchar', { name: '_sort_id', length: 100, nullable: true })
	public sortId!: string | null;

	@Column('json', { name: '_sort_fields', nullable: true })
	public sortFields!: Array<SortField> | null;

	@Column('json', { name: '_columns', nullable: true })
	public columns!: Array<unknown> | null;

	/**
	 * Overrides the base list's `activeFilters` array (which filters appear on
	 * the list). null = inherit from the bundled config.
	 */
	@Column('json', { name: '_active_filters', nullable: true })
	public activeFilters!: Array<string> | null;

	/**
	 * Per-filter rule overrides — keyed by filter id. Each value merges
	 * field-by-field over the bundled list's FilterRule via
	 * FilterRulesService.resolveFilterRule (override wins where defined).
	 * null = inherit all rules from the bundled config.
	 */
	@Column('json', { name: '_filter_rules', nullable: true })
	public filterRules!: Record<string, SavedSearchFilterRule> | null;

	/**
	 * Misc per-list ListConfiguration knobs the admin can override, kept in
	 * one JSON blob so adding new tabs (Click Action, View Mode, etc.) does
	 * not require new columns. Currently used for `showFiltersByDefault`.
	 */
	@Column('json', { name: '_list_overrides', nullable: true })
	public listOverrides!: Record<string, unknown> | null;

	@Column('enum', {
		name: '_visibility',
		enum: ['private', 'role', 'organization'],
		default: 'private',
	})
	public visibility!: ListSavedSearchVisibility;

	@Column('tinyint', { name: '_is_active', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	@Column('int', { name: '_fk_manager_user__id', unsigned: true })
	public fkManagerUserId!: number;

	@Column('tinyint', { name: '_allow_edit', width: 1, default: () => "'0'" })
	public allowEdit!: boolean;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_manager_user__id', referencedColumnName: 'id' }])
	public fkManagerUser!: UserEntity | null;

	@OneToMany(
		'ListSavedSearchRoleEntity',
		(role: ListSavedSearchRoleEntity) => role.fkSavedSearch,
	)
	public sharedRoles!: Array<ListSavedSearchRoleEntity>;
}
